import {Box, Typography} from '@mui/material';
import {useRouter} from '@tanstack/react-router';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'tolgee';

import {useTrackEvent} from '~hooks/analytics.hooks';
import {useHotkeysWhenDialogsAreClosed} from '~hooks/misc.hooks';

import {SparklesIcon} from 'src/icons';
import {AGENT_TILE_GRADIENT, getAgentForPathname} from './askAiAgents';
import {useAskAi} from './AskAiContext';
import {useDraggableFloatingButton} from './useDraggableFloatingButton';
import {useFloatingBottomOffset} from './useLayoutMetrics';

const INTRO_VISIBLE_MS = 4000;

const LEG_MS = 1150;
const WIDTH_EXPAND_EASING = 'cubic-bezier(0.2, 0, 0.275, 1)';
const WIDTH_COLLAPSE_EASING = 'cubic-bezier(0.5, 0, 0, 1)';
const LABEL_FADE = '500ms cubic-bezier(0, 0, 0, 1) 150ms';
const CARD_SHADOW =
  '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)';
const CARD_SHADOW_CLEAR =
  '0px 3px 5px -1px rgba(0,0,0,0),0px 6px 10px 0px rgba(0,0,0,0),0px 1px 18px 0px rgba(0,0,0,0)';
const CARD_WHITE = 'rgba(255,255,255,1)';
const CARD_WHITE_CLEAR = 'rgba(255,255,255,0)';

const iconTiltExpand = {
  '0%': {transform: 'rotate(0deg)', animationTimingFunction: 'cubic-bezier(0, 0, 0.236, 0.568)'},
  '13.04%': {transform: 'rotate(-10.66deg)', animationTimingFunction: 'cubic-bezier(0.349, 0.688, 0.706, 0.999)'},
  '27.06%': {transform: 'rotate(-13.93deg)', animationTimingFunction: 'cubic-bezier(0.566, 0.001, 0.644, 0.563)'},
  '42.04%': {transform: 'rotate(-7.12deg)', animationTimingFunction: 'cubic-bezier(0.265, 0.477, 0.625, 0.79)'},
  '57.01%': {transform: 'rotate(-2.48deg)', animationTimingFunction: 'cubic-bezier(0.237, 0.524, 0.557, 0.881)'},
  '86.96%': {transform: 'rotate(-0.13deg)', animationTimingFunction: 'cubic-bezier(0.306, 0.654, 0.638, 1)'},
  '100%': {transform: 'rotate(0deg)'},
};

const iconTiltCollapse = {
  '0%': {transform: 'rotate(0deg)', animationTimingFunction: 'cubic-bezier(0.434, 0, 0.755, 0.365)'},
  '13.04%': {transform: 'rotate(-1.39deg)', animationTimingFunction: 'cubic-bezier(0.519, 0.236, 0.782, 0.558)'},
  '22.63%': {transform: 'rotate(-7.24deg)', animationTimingFunction: 'cubic-bezier(0.256, 0.351, 0.537, 0.999)'},
  '32.21%': {transform: 'rotate(-15.89deg)', animationTimingFunction: 'cubic-bezier(0.369, 0.002, 0.386, 0.64)'},
  '45.90%': {transform: 'rotate(-5.59deg)', animationTimingFunction: 'cubic-bezier(0.234, 0.428, 0.549, 0.748)'},
  '59.58%': {transform: 'rotate(-2.29deg)', animationTimingFunction: 'cubic-bezier(0.238, 0.432, 0.558, 0.75)'},
  '86.96%': {transform: 'rotate(-0.26deg)', animationTimingFunction: 'cubic-bezier(0.306, 0.654, 0.638, 1)'},
  '100%': {transform: 'rotate(0deg)'},
};

const cardPopExpand = {
  '0%': {
    transform: 'scaleX(1)',
    backgroundColor: CARD_WHITE_CLEAR,
    boxShadow: CARD_SHADOW_CLEAR,
    animationTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
  },
  '17%': {
    backgroundColor: CARD_WHITE,
    boxShadow: CARD_SHADOW,
    animationTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
  },
  '35.5%': {transform: 'scaleX(1.0917)', animationTimingFunction: 'cubic-bezier(0.2, 0, 0.275, 1)'},
  '100%': {transform: 'scaleX(1)', backgroundColor: CARD_WHITE, boxShadow: CARD_SHADOW},
};

const cardPopCollapse = {
  '0%': {
    backgroundColor: CARD_WHITE,
    boxShadow: CARD_SHADOW,
    animationTimingFunction: 'cubic-bezier(0.5, 0, 0, 1)',
  },
  '13.04%': {
    backgroundColor: CARD_WHITE,
    boxShadow: CARD_SHADOW,
    animationTimingFunction: 'cubic-bezier(0.5, 0, 0, 1)',
  },
  '56.5%': {backgroundColor: CARD_WHITE_CLEAR, boxShadow: CARD_SHADOW_CLEAR},
  '100%': {backgroundColor: CARD_WHITE_CLEAR, boxShadow: CARD_SHADOW_CLEAR},
};

type Motion = {dir: 'expand' | 'collapse'; nonce: number};

export function AskAiFloatingButton() {
  const {t} = useTranslation('common');
  const router = useRouter();
  const pathname = router.state.location.pathname;
  const agent = getAgentForPathname(pathname);
  const {isEnabled, isQualityEnabled, isOpen, agent: openAgent, openPanel, togglePanel} = useAskAi();
  const trackEvent = useTrackEvent();
  const [isHovered, setIsHovered] = useState(false);
  const [isIntroExpanded, setIsIntroExpanded] = useState(false);
  const bottomOffset = useFloatingBottomOffset();
  const {elementRef, position, isDragging, shouldIgnoreClick, dragHandlers} = useDraggableFloatingButton({
    right: 24,
    bottom: bottomOffset,
  });

  const isVisionVisible = agent === 'vision' && isEnabled && !(isOpen && openAgent === 'vision');
  const isQualityVisible = agent === 'quality' && isQualityEnabled && !(isOpen && openAgent === 'quality');
  const isVisible = isVisionVisible || isQualityVisible;
  const isExpanded = isHovered || isIntroExpanded || isDragging;

  const hasPlayedIntroRef = useRef(false);
  const previousExpandedRef = useRef(isExpanded);
  const [motion, setMotion] = useState<Motion | null>(null);

  useEffect(() => {
    if (previousExpandedRef.current === isExpanded) {
      return;
    }
    previousExpandedRef.current = isExpanded;
    setMotion((current) => ({dir: isExpanded ? 'expand' : 'collapse', nonce: (current?.nonce ?? 0) + 1}));
  }, [isExpanded]);

  useHotkeysWhenDialogsAreClosed(
    'shift+k',
    () => {
      if (agent === 'vision' && isEnabled) {
        togglePanel('vision');
      } else if (agent === 'quality' && isQualityEnabled) {
        togglePanel('quality');
      }
    },
    [agent, isEnabled, isQualityEnabled, togglePanel],
  );

  useEffect(() => {
    if (!isVisible || hasPlayedIntroRef.current) {
      return;
    }
    hasPlayedIntroRef.current = true;
    setIsIntroExpanded(true);
    const timeout = window.setTimeout(() => setIsIntroExpanded(false), INTRO_VISIBLE_MS);
    return () => window.clearTimeout(timeout);
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      return;
    }
    setIsHovered(false);
    setIsIntroExpanded(false);
    setMotion(null);
    previousExpandedRef.current = false;
  }, [isVisible]);

  if (!isVisible || !agent) {
    return null;
  }

  const label =
    agent === 'vision' ? t('askVisionAgent', 'Ask Vision Agent') : t('askQualityAgent', 'Ask Process Agent');
  const tagline =
    agent === 'vision'
      ? t('visionAgentTagline', 'Your 24/7 Vision Engineer')
      : t('qualityAgentTagline', 'Your 24/7 Process Engineer');
  const widthEasing = isExpanded ? WIDTH_EXPAND_EASING : WIDTH_COLLAPSE_EASING;
  const motionKey = motion ? `${motion.dir}-${motion.nonce}` : 'initial';

  const handleClick = () => {
    if (shouldIgnoreClick()) {
      return;
    }
    if (agent === 'vision') {
      trackEvent('ask_ai_widget_open', {});
      openPanel('vision');
    } else {
      trackEvent('quality_agent_open', {});
      openPanel('quality');
    }
  };

  return (
    <Box
      ref={elementRef}
      className='askAiFloatingButton'
      role='button'
      tabIndex={0}
      aria-label={label}
      onClick={handleClick}
      {...dragHandlers}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid='ask-ai-floating-button'
      data-expanded={isExpanded}
      sx={(theme) => ({
        position: 'fixed',
        right: position.right,
        bottom: position.bottom,
        zIndex: theme.zIndex.modal + 1,
        display: 'flex',
        alignItems: 'center',
        gap: isExpanded ? 1.5 : 0,
        padding: isExpanded ? theme.spacing(0.75, 2, 0.75, 0.75) : 0,
        cursor: isDragging ? 'grabbing' : 'pointer',
        touchAction: 'none',
        userSelect: 'none',
        transition: [`gap ${LEG_MS}ms ${widthEasing}`, `padding ${LEG_MS}ms ${widthEasing}`].join(', '),
      })}
    >
      <Box
        key={`card-${motionKey}`}
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: 3,
          transformOrigin: 'right center',
          backgroundColor: isExpanded ? CARD_WHITE : CARD_WHITE_CLEAR,
          boxShadow: isExpanded ? CARD_SHADOW : CARD_SHADOW_CLEAR,
          animation: motion ? `${LEG_MS}ms both` : 'none',
          animationName: motion ? (motion.dir === 'expand' ? 'askAiCardPopExpand' : 'askAiCardPopCollapse') : 'none',
          '@keyframes askAiCardPopExpand': cardPopExpand,
          '@keyframes askAiCardPopCollapse': cardPopCollapse,
        }}
      />
      <Box
        sx={(theme) => ({
          position: 'relative',
          width: 40,
          height: 40,
          borderRadius: 2.5,
          background: AGENT_TILE_GRADIENT[agent],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.palette.common.white,
          boxShadow: isExpanded ? 'none' : theme.shadows[6],
          flexShrink: 0,
          transition: 'box-shadow 200ms ease',
          '& svg path': {fill: 'currentColor'},
          '& svg': {
            animation: motion ? `${LEG_MS}ms both` : 'none',
            animationName: motion
              ? motion.dir === 'expand'
                ? 'askAiIconTiltExpand'
                : 'askAiIconTiltCollapse'
              : 'none',
          },
          '@keyframes askAiIconTiltExpand': iconTiltExpand,
          '@keyframes askAiIconTiltCollapse': iconTiltCollapse,
        })}
      >
        <SparklesIcon key={`icon-${motionKey}`} />
      </Box>
      <Box
        sx={{
          position: 'relative',
          maxWidth: isExpanded ? 260 : 0,
          opacity: isExpanded ? 1 : 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          transition: [`max-width ${LEG_MS}ms ${widthEasing}`, `opacity ${LABEL_FADE}`].join(', '),
        }}
      >
        <Typography variant='body2' fontWeight={600}>
          {label}
        </Typography>
        <Typography variant='caption' color='text.secondary' display='block'>
          {tagline}
        </Typography>
      </Box>
    </Box>
  );
}
